import {Box, CircularProgress, Switch, Typography} from '@mui/material';
import {LockOutlined} from '@mui/icons-material';
import {FC, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {ocppApi} from 'src/api';
import {useAppSelector} from 'src/app/hooks';
import {ChargePointStatus, ConfigurationStatusEnum} from 'src/enums';
import {CHARGE_POINT_STATUS_TYPE, CONFIGURATIONS_TYPE} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {CableLockToggleType} from './CableLockToggle.types';
import {badgeSx, captionSx, controlSx, infoSx, spinnerSx, titleSx, wrapperSx} from './CableLockToggle.sx';

const UNLOCK_KEY = 'UnlockConnectorOnEVSideDisconnect';
const CONFIRM_TIMEOUT_MS = 30000;

export const CableLockToggle: FC<CableLockToggleType> = ({
    chargerId,
    businessId,
    companyId,
    chargerStatus,
}) => {
    const intl = useIntl();
    // Both endpoints answer to `chargers.cableLock` rather than to the general configuration
    // permissions: the switch writes one key and reads one key, and needing the right to reprogram the
    // device to release your own cable was the reason for splitting them out.
    const [trigger] = ocppApi.useLazyGetCableLockQuery();
    const [setCableLock] = ocppApi.useSetCableLockMutation();

    const CONFIGURATIONS: CONFIGURATIONS_TYPE[] = useAppSelector(
        (state) => state.common.CONFIGURATIONS
    );
    const CHARGE_POINT_STATUS: CHARGE_POINT_STATUS_TYPE[] = useAppSelector(
        (state) => state.common.CHARGE_POINT_STATUS
    );

    const [locked, setLocked] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [supported, setSupported] = useState(false);
    const [readonly, setReadonly] = useState(false);
    const [pending, setPending] = useState(false);

    const pendingRef = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearPending = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        pendingRef.current = false;
        setPending(false);
    };

    // One-time initial read of the current value over REST
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const list = await trigger({
                    chargerId,
                    businessId,
                    ...(companyId && {companyId}),
                }).unwrap();
                if (cancelled) {
                    return;
                }
                const item = list.find((c) => c.key === UNLOCK_KEY);
                setSupported(Boolean(item));
                if (item) {
                    setReadonly(item.readonly);
                    setLocked(item.value.toLowerCase() !== 'true');
                }
            } catch {
                // leave as unsupported / unknown
            } finally {
                if (!cancelled) {
                    setLoaded(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [chargerId, businessId, companyId, trigger]);

    // Live confirmation: the WS-fed CONFIGURATIONS slice carries the applied value + status
    useEffect(() => {
        const entry = CONFIGURATIONS.find((c) => c.chargerId === chargerId);
        if (!entry) {
            return;
        }
        const keyItem = entry.configurations?.find((c) => c.key === UNLOCK_KEY);
        if (entry.status === ConfigurationStatusEnum.ACCEPTED && keyItem) {
            setSupported(true);
            setLocked(keyItem.value.toLowerCase() !== 'true');
            clearPending();
        } else if (pendingRef.current && entry.status !== ConfigurationStatusEnum.ACCEPTED) {
            clearPending();
            NotifyError(intl.formatMessage({id: 'cableLockChangeFailed'}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CONFIGURATIONS]);

    useEffect(
        () => () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        []
    );

    const livePoint = CHARGE_POINT_STATUS.find((s) => s.chargerId === chargerId);
    const offline = (livePoint?.status ?? chargerStatus) === ChargePointStatus.OFFLINE;
    const disabled = !loaded || !supported || readonly || offline || pending;

    const caption = !loaded
        ? ''
        : offline
            ? intl.formatMessage({id: 'chargerIsOffline'})
            : !supported
                ? intl.formatMessage({id: 'cableLockNotSupported'})
                : intl.formatMessage({id: locked ? 'cableLocked' : 'cableUnlocked'});

    const handleToggle = async () => {
        if (disabled) {
            return;
        }
        // The inversion the OCPP key needs — "unlock on disconnect" is the opposite of "locked" — is
        // done on the server now, so what travels is what the switch means.
        const nextLocked = !locked;
        pendingRef.current = true;
        setPending(true);
        timeoutRef.current = setTimeout(() => {
            clearPending();
            NotifyError(intl.formatMessage({id: 'cableLockChangeFailed'}));
        }, CONFIRM_TIMEOUT_MS);
        try {
            await setCableLock({
                chargerId,
                businessId,
                locked: nextLocked,
                ...(companyId && {companyId}),
            }).unwrap();
        } catch (error) {
            clearPending();
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        }
    };

    return (
        <Box sx={wrapperSx}>
            <Box sx={controlSx}>
                <Switch
                    size="small"
                    checked={locked}
                    disabled={disabled}
                    onChange={handleToggle}
                    sx={pending ? {visibility: 'hidden'} : undefined}
                />
                {pending && <CircularProgress size={16} sx={spinnerSx}/>}
            </Box>
            <Box sx={infoSx}>
                <Box sx={badgeSx}>
                    <LockOutlined/>
                </Box>
                <Box sx={{minWidth: 0}}>
                    <Typography sx={titleSx}>
                        {intl.formatMessage({id: 'cableLock'})}
                    </Typography>
                    {/* The state in words under the label: a switch alone does not say whether it
                        means locked or unlocked, and it is also where "offline" and "not supported"
                        are explained. Always rendered — a non-breaking space while the answer is on
                        its way — so the line exists before it has anything to say. */}
                    <Typography sx={captionSx}>{caption || '\u00A0'}</Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default CableLockToggle;
